// This file is part of MinIO Console Server
// Copyright (c) 2021 MinIO, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <http://www.gnu.org/licenses/>.

import { RedirectRule } from "api/consoleApi";
import { ApplicationLogo, Button, Loader, RefreshIcon, ThemedLogo } from "mds";
import { Fragment, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { AppState, useAppDispatch } from "../../store";
import { setHelpName } from "../../systemSlice";
import MainError from "../Console/Common/MainError/MainError";
import { loginStrategyType } from "./login.types";
import { redirectRules } from "./login.utils";
import { resetForm } from "./loginSlice";
import { getFetchConfigurationAsync } from "./loginThunks";
import StrategyForm from "./StrategyForm";

export const getTargetPath = () => {
  let targetPath = "/browser";
  if (
    localStorage.getItem("redirect-path") &&
    localStorage.getItem("redirect-path") !== ""
  ) {
    targetPath = `${localStorage.getItem("redirect-path")}`;
    localStorage.setItem("redirect-path", "");
  }
  return targetPath;
};

// Styled component that always uses dark theme for login page
const CenteredLoginWrapper = styled.div(() => ({
  minHeight: "100vh",
  background: "linear-gradient(118deg, #031021 15.5%, #072445 50%, #063A70 84.5%)", // Dark theme background
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  position: "relative" as const,

  "& .loginCard": {
    width: "100%",
    maxWidth: "440px",
    backgroundColor: "#283140", // Dark theme card background
    borderRadius: "5px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 25px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
    position: "relative" as const,
    zIndex: 1,
  },
  "& button:has(svg)": {
    backgroundColor: "#FFFFFF"
  },
  "& (button:has(svg))):hover": {
    backgroundColor: "#ff6028"
  },

  "&button:has(svg) > svg:hover": {
    fill: "#FFFFFF",
  },

  "& .logoSection": {
    padding: "40px 40px 20px 40px",
    textAlign: "center" as const,
    backgroundColor: "#283140", // Dark theme card background
    borderBottom: "1px solid #545D6A", // Dark theme divider
    "& svg": {
      maxWidth: "180px",
      height: "auto",
    },
  },

  "& .formSection": {
    padding: "40px",
    backgroundColor: "#283140", // Dark theme card background
    "& .welcomeTitle": {
      fontSize: "28px",
      fontWeight: "700",
      color: "#C4C9D0", // Dark theme font color
      marginBottom: "8px",
      textAlign: "center" as const,
      lineHeight: "1.2",
    },
    "& .welcomeSubtitle": {
      fontSize: "16px",
      color: "#8E98A9", // Dark theme muted text
      marginBottom: "32px",
      textAlign: "center" as const,
      lineHeight: "1.4",
    },
  },

  "& .footer": {
    padding: "24px 40px",
    backgroundColor: "#283140", // Dark theme card background
    borderTop: "1px solid #545D6A", // Dark theme divider
    textAlign: "center" as const,
    "& a": {
      color: "#85B3EE", // Dark theme footer elements
      fontSize: "14px",
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    "& .separator": {
      color: "#85B3EE", // Dark theme footer elements
      marginLeft: "8px",
      marginRight: "8px",
    },
  },

  "& .errorContainer": {
    textAlign: "center" as const,
    padding: "20px",
    "& .loadingLoginStrategy": {
      textAlign: "center" as const,
      width: 40,
      height: 40,
      margin: "0 auto 16px auto",
    },
    "& .buttonRetry": {
      display: "flex",
      justifyContent: "center",
      marginTop: "24px",
    },
    "& .errorTitle": {
      color: "#FF3958", // Dark theme danger color
      fontSize: "18px",
      fontWeight: "600",
      marginBottom: "8px",
    },
    "& .errorMessage": {
      color: "#d8d8d8ff", // Dark theme muted text
      fontSize: "14px",
      lineHeight: "1.4",
      marginBottom: "24px",
    },
  },
}));

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const loginStrategy = useSelector(
    (state: AppState) => state.login.loginStrategy,
  );
  const loadingFetchConfiguration = useSelector(
    (state: AppState) => state.login.loadingFetchConfiguration,
  );
  const navigateTo = useSelector((state: AppState) => state.login.navigateTo);

  useEffect(() => {
    if (navigateTo !== "") {
      dispatch(resetForm());
      navigate(navigateTo);
    }
  }, [navigateTo, dispatch, navigate]);

  useEffect(() => {
    if (loadingFetchConfiguration) {
      dispatch(getFetchConfigurationAsync());
    }
  }, [loadingFetchConfiguration, dispatch]);

  let loginComponent;

  switch (loginStrategy.loginStrategy) {
    case loginStrategyType.redirect:
    case loginStrategyType.form: {
      let redirectItems: RedirectRule[] = [];

      if (
        loginStrategy.redirectRules &&
        loginStrategy.redirectRules.length > 0
      ) {
        redirectItems = [...loginStrategy.redirectRules].sort(redirectRules);
      }

      loginComponent = <StrategyForm redirectRules={redirectItems} />;
      break;
    }
    default:
      loginComponent = (
        <div className="errorContainer">
          {loadingFetchConfiguration ? (
            <Fragment>
              <Loader className={"loadingLoginStrategy"} />
              <p style={{ color: "white", margin: 0 }}>Подключение к серверу...</p>
            </Fragment>
          ) : (
            <Fragment>
              <div className="errorTitle">Ошибка подключения</div>
              <div className="errorMessage">
                Возникла ошибка
                <br />
                Нет связи с бэкендом.
              </div>
              <div className={"buttonRetry"}>
                <Button
                  onClick={() => {
                    dispatch(getFetchConfigurationAsync());
                  }}
                  icon={<RefreshIcon />}
                  iconLocation={"end"}
                  variant="regular"
                  id="retry"
                  label={"Повторить"}
                />
              </div>
            </Fragment>
          )}
        </div>
      );
  }

  useEffect(() => {
    dispatch(setHelpName("login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <MainError />
      <CenteredLoginWrapper>
        <div className="loginCard">
          {/* Logo Section */}
          <div className="logoSection">
            <Fragment>
              {/* <svg viewBox="0 0 167 28" fill="#fff">
                <path d="M163.404 16.0112C162.729 17.1414 161.515 17.8998 160.103 17.8998C157.997 17.8998 156.263 16.1625 156.263 14.0162C156.263 11.921 157.997 10.1666 160.103 10.1666C161.515 10.1666 162.73 10.9258 163.405 12.0425C163.473 12.1544 163.615 12.1947 163.731 12.1339L165.607 11.1525C165.669 11.1201 165.71 11.0693 165.729 11.002C165.749 10.9349 165.742 10.8699 165.707 10.809C164.599 8.8713 162.509 7.56039 160.103 7.56039C156.552 7.56039 153.646 10.473 153.646 14.0162C153.646 17.6102 156.552 20.5059 160.103 20.5059C162.527 20.5059 164.602 19.2094 165.708 17.272C165.743 17.2116 165.751 17.1471 165.731 17.0802C165.712 17.0134 165.672 16.9628 165.61 16.9299L163.733 15.9199C163.617 15.8574 163.472 15.8975 163.404 16.0112Z" fill="var(--_--primary)"></path><path d="M138.108 20.0969H140.229C140.366 20.0969 140.477 19.9857 140.477 19.8487V12.4831L143.791 20.0969H146.135L149.449 12.3978V19.8487C149.449 19.9857 149.56 20.0969 149.697 20.0969H151.819C151.955 20.0969 152.066 19.9857 152.066 19.8487V8.21736C152.066 8.08074 151.955 7.96928 151.819 7.96928H149.021C148.854 7.96928 148.711 8.06259 148.644 8.21541L144.963 16.5542L141.299 8.21606C141.232 8.06259 141.089 7.96928 140.921 7.96928H138.108C137.971 7.96928 137.86 8.08074 137.86 8.21736V19.8487C137.86 19.9857 137.971 20.0969 138.108 20.0969Z" fill="var(--_--primary)"></path><path d="M126.519 20.0969H135.726C135.863 20.0969 135.974 19.9857 135.974 19.8487V17.7561C135.974 17.6195 135.863 17.5081 135.726 17.5081H128.888V15.2767H133.738C133.874 15.2767 133.986 15.1653 133.986 15.0285V12.9697C133.986 12.8331 133.874 12.7215 133.738 12.7215H128.888V10.5412H135.726C135.863 10.5412 135.974 10.4297 135.974 10.2931V8.21736C135.974 8.08074 135.863 7.96928 135.726 7.96928H126.519C126.382 7.96928 126.271 8.08074 126.271 8.21736V19.8487C126.271 19.9857 126.382 20.0969 126.519 20.0969Z" fill="var(--_--primary)"></path><path d="M118.141 20.0969H120.246C120.383 20.0969 120.494 19.9857 120.494 19.8487V10.5584H124.444C124.58 10.5584 124.691 10.4468 124.691 10.3102V8.21736C124.691 8.08074 124.58 7.96928 124.444 7.96928H113.91C113.774 7.96928 113.663 8.08074 113.663 8.21736V10.3102C113.663 10.4468 113.774 10.5584 113.91 10.5584H117.894V19.8487C117.894 19.9857 118.005 20.0969 118.141 20.0969Z" fill="var(--_--primary)"></path><path d="M110.421 16.0112C109.746 17.1414 108.531 17.8998 107.12 17.8998C105.013 17.8998 103.28 16.1625 103.28 14.0162C103.28 11.921 105.013 10.1666 107.12 10.1666C108.532 10.1666 109.747 10.9258 110.422 12.0425C110.489 12.1544 110.632 12.1947 110.748 12.1339L112.623 11.1525C112.685 11.1201 112.727 11.0693 112.746 11.002C112.766 10.9349 112.758 10.8699 112.724 10.809C111.616 8.8713 109.526 7.56039 107.12 7.56039C103.569 7.56039 100.663 10.473 100.663 14.0162C100.663 17.6102 103.569 20.5059 107.12 20.5059C109.543 20.5059 111.619 19.2094 112.725 17.272C112.759 17.2116 112.767 17.1471 112.748 17.0802C112.729 17.0134 112.688 16.9628 112.627 16.9299L110.75 15.9199C110.634 15.8574 110.489 15.8975 110.421 16.0112Z" fill="var(--_--primary)"></path><path d="M88.0473 20.0969H90.3057C90.4984 20.0969 90.6643 20.0114 90.7762 19.8539L96.4659 11.853V19.8487C96.4659 19.9857 96.5769 20.0969 96.7134 20.0969H98.8353C98.9719 20.0969 99.0829 19.9857 99.0829 19.8487V8.21736C99.0829 8.08074 98.9719 7.96928 98.8353 7.96928H96.5933C96.4008 7.96928 96.2351 8.05449 96.1233 8.21152L90.4165 16.2136V8.21736C90.4165 8.08074 90.3053 7.96928 90.169 7.96928H88.0473C87.911 7.96928 87.7997 8.08074 87.7997 8.21736V19.8487C87.7997 19.9857 87.911 20.0969 88.0473 20.0969Z" fill="var(--_--primary)"></path><path d="M83.9123 16.0112C83.2372 17.1414 82.0227 17.8998 80.6118 17.8998C78.5045 17.8998 76.7716 16.1625 76.7716 14.0162C76.7716 11.921 78.5045 10.1666 80.6118 10.1666C82.0234 10.1666 83.2381 10.9258 83.9132 12.0425C83.981 12.1544 84.1235 12.1947 84.2395 12.1339L86.1146 11.1525C86.1765 11.1201 86.218 11.0693 86.2375 11.002C86.2571 10.9349 86.2496 10.8699 86.2148 10.809C85.1077 8.8713 83.0171 7.56039 80.6118 7.56039C77.0603 7.56039 74.1548 10.473 74.1548 14.0162C74.1548 17.6102 77.0603 20.5059 80.6118 20.5059C83.0348 20.5059 85.1107 19.2096 86.2163 17.272C86.2507 17.2116 86.2586 17.1471 86.2395 17.0802C86.2202 17.0134 86.1795 16.9628 86.1185 16.9299L84.2417 15.9199C84.125 15.8574 83.9801 15.8975 83.9123 16.0112Z" fill="var(--_--primary)"></path><path d="M37.2048 20.0971H39.3268C39.4633 20.0971 39.5743 19.9859 39.5743 19.849V15.3448H41.7154L44.8056 19.8464C44.9169 20.0084 45.0854 20.0971 45.2815 20.0971H47.833C47.8944 20.0971 47.9469 20.0653 47.9754 20.011C48.0039 19.9569 48.0004 19.8953 47.9658 19.8446L43.9754 14.0162L47.9646 8.22211C47.9994 8.17146 48.003 8.11 47.9748 8.05557C47.946 8.00103 47.8933 7.96928 47.8321 7.96928H45.2815C45.0854 7.96928 44.9169 8.05806 44.8056 8.21995L41.7154 12.7215H39.5743V8.21736C39.5743 8.08074 39.4633 7.96928 39.3268 7.96928H37.2048C37.0685 7.96928 36.9575 8.08074 36.9575 8.21736V19.849C36.9575 19.9859 37.0685 20.0971 37.2048 20.0971Z" fill="var(--_--secondary)"></path><path d="M61.5386 20.0971H63.797C63.9898 20.0971 64.1557 20.0116 64.2673 19.8541L69.9572 11.853V19.849C69.9572 19.9859 70.0685 20.0971 70.2048 20.0971H72.3267C72.463 20.0971 72.5742 19.9859 72.5742 19.849V8.21736C72.5742 8.08074 72.463 7.96928 72.3267 7.96928H70.0844C69.892 7.96928 69.7265 8.05449 69.6147 8.21152L63.9079 16.2136V8.21736C63.9079 8.08074 63.7966 7.96928 63.6603 7.96928H61.5386C61.4023 7.96928 61.291 8.08074 61.291 8.21736V19.849C61.291 19.9859 61.4023 20.0971 61.5386 20.0971Z" fill="var(--_--secondary)"></path><path d="M49.9496 20.0971H59.1572C59.2935 20.0971 59.4048 19.9859 59.4048 19.849V17.7561C59.4048 17.6195 59.2935 17.5081 59.1572 17.5081H52.3188V15.2767H57.1691C57.3053 15.2767 57.4166 15.1653 57.4166 15.0285V12.9697C57.4166 12.8331 57.3053 12.7215 57.1691 12.7215H52.3188V10.5412H59.1572C59.2935 10.5412 59.4048 10.4297 59.4048 10.2931V8.21736C59.4048 8.08074 59.2935 7.96928 59.1572 7.96928H49.9496C49.8131 7.96928 49.702 8.08074 49.702 8.21736V19.849C49.702 19.9859 49.8131 20.0971 49.9496 20.0971Z" fill="var(--_--secondary)"></path><path d="M68.4023 6.57198L69.9295 4.44675C69.9648 4.39794 69.9687 4.33724 69.9415 4.28346C69.9144 4.22967 69.8615 4.19781 69.8021 4.19781H64.1553C64.0191 4.19781 63.9079 4.30981 63.9079 4.44589V6.57306C63.9079 6.7087 64.0191 6.82113 64.1553 6.82113H67.9169C68.1153 6.82113 68.2862 6.73376 68.4023 6.57198Z" fill="var(--_--primary)"></path><path d="M14.144 9.33926H26.0085C26.6876 9.33926 27.3027 8.82518 27.3815 8.1909L27.9803 3.39548C28.0589 2.76152 27.5726 2.24744 26.8933 2.24744H20.9057C19.681 2.24744 18.6283 2.62101 17.6763 3.39256C16.0185 4.73608 14.5815 6.35716 13.4935 8.19338C13.3508 8.43444 13.348 8.71243 13.4863 8.95597C13.6245 9.1994 13.8647 9.33926 14.144 9.33926Z" fill="var(--_--primary)"></path><path d="M5.17732 2.24744C4.49823 2.24744 3.88324 2.7613 3.80426 3.39548L3.20554 8.1909C3.12646 8.82518 3.61302 9.33926 4.29233 9.33926H10.2486C11.0746 9.33926 11.7837 8.91687 12.178 8.18971C13.1464 6.40393 14.4219 4.79451 15.9071 3.41092C16.1126 3.21944 16.1773 2.93562 16.0749 2.6735C15.9724 2.41138 15.7323 2.24744 15.4514 2.24744H5.17732Z" fill="var(--_--secondary)"></path><path d="M12.5551 17.579H17.8475C18.5266 17.579 19.1419 17.0649 19.2205 16.4308L19.8192 11.6353C19.8981 11.0014 19.4121 10.4873 18.7324 10.4873H13.5198C12.7933 10.4873 12.1768 10.9449 11.9651 11.6418C11.4947 13.1913 11.2751 14.8151 11.353 16.4312C11.384 17.0744 11.9127 17.579 12.5551 17.579Z" fill="var(--_--primary)"></path><path d="M4.14914 10.4873C3.46983 10.4873 2.85473 11.0014 2.77587 11.6353L2.17736 16.431C2.09795 17.0649 2.58484 17.579 3.26394 17.579H9.11497C9.42408 17.579 9.6901 17.4649 9.90396 17.241C10.1176 17.0172 10.2195 16.7454 10.206 16.4357C10.1342 14.821 10.3355 13.1972 10.7714 11.6405C10.8506 11.3582 10.7984 11.0804 10.6215 10.8466C10.4446 10.6129 10.1923 10.4873 9.89976 10.4873H4.14914Z" fill="var(--_--secondary)"></path><path d="M9.24221 18.7271H3.12075C2.44165 18.7271 1.82677 19.2411 1.74736 19.8754L1.14896 24.6707C1.06999 25.3047 1.55633 25.8189 2.23565 25.8189H12.8782C13.1545 25.8189 13.3918 25.6674 13.5081 25.4161C13.6247 25.1647 13.5878 24.8862 13.4099 24.674C12.2273 23.2646 11.3366 21.6427 10.796 19.8781C10.5831 19.1832 9.96721 18.7271 9.24221 18.7271Z" fill="var(--_--secondary)"></path><path d="M23.9516 25.8189C24.6309 25.8189 25.2458 25.3047 25.3252 24.6707L25.9234 19.8754C26.0028 19.2411 25.5162 18.7271 24.8363 18.7271H12.8218C12.5335 18.7271 12.2846 18.8549 12.1164 19.0899C11.9483 19.3249 11.907 19.6018 11.9993 19.8761C12.6091 21.6865 13.6217 23.3113 14.9492 24.6693C15.6948 25.4321 16.6123 25.8189 17.678 25.8189H23.9516Z" fill="var(--_--primary)"></path>
              </svg> */}
            </Fragment>
            {/* <KeysystemsLogo /> */}
              
            {/* <ThemedLogo variant="keysystems_night"/> */}
            <ApplicationLogo
              applicationName="minio"
              subVariant="night"
            />
          </div>

          {/* Form Section */}
          <div className="formSection">
            {loginComponent}
            {/* <h1 className="welcomeTitle">С возвращением!</h1> */}
            {/* <p className="welcomeSubtitle">
              Войдите для получения доступа к консоли объектного хранилища
            </p> */}
          </div>
        </div>
      </CenteredLoginWrapper>
    </Fragment>
  );
};

export default Login;
